import paper from "paper";
import { PaperOffset } from "paperjs-offset";
import {
  isVisible,
  pieceStitchLines,
  projectConstructionLines,
  type Project,
  type Decoration,
} from "../model/Project";
import { pathsFromPlacements, stitchHolePlacements } from "../model/stitching";
import { placedStitchHoles } from "../model/placedStitchHoles";
import type { VectorExportSettings } from "./sceneToSvg";
import { applyPieceLayout } from "../model/pieceTransform";
import type { StampLibrary } from "../stamps/StampLibrary";
import { triggerDownload } from "./download";
import { toModernDxf, type DxfEntity, type LayerName } from "./dxfSerialize";

const SAMPLES_PER_CURVE = 24;

/**
 * Modern DXF (AutoCAD 2007 / AC1021, LWPOLYLINE) for full CAD / vector tools
 * such as Affinity Designer. Geometry stays in millimetres.
 */
export function exportDxf(
  project: Project,
  library: StampLibrary,
  options: { vector?: VectorExportSettings } = {},
): Blob {
  const entities = gatherEntities(project, library, options.vector);
  return new Blob([toModernDxf(entities)], { type: "application/dxf" });
}

/**
 * Walk the project into a flat list of DXF entities. Curves are approximated
 * as polylines; the y axis is negated so the export is right-handed (CAD
 * convention, y-up). Construction (guide) lines are included on their own
 * dashed layer, matching the SVG export (#444).
 */
function gatherEntities(
  project: Project,
  library: StampLibrary,
  vector?: VectorExportSettings,
): DxfEntity[] {
  const entities: DxfEntity[] = [];
  // Optimized export (#572): content gating + the stitch-geometry mode.
  // DXF colors are the layer conventions themselves (CUT red, HOLE blue,
  // …), so the SVG-side laser color settings don't apply here; category →
  // layer stays fixed and the CAM tool maps layers to operations.
  const includePattern = vector ? vector.content.pattern : true;
  const includeConstruction = vector ? vector.content.constructionLines : true;
  const stitchMode = vector ? vector.content.stitching : "working-holes";

  const scope = new paper.PaperScope();
  const c = document.createElement("canvas");
  c.width = 1;
  c.height = 1;
  scope.setup(c);
  scope.activate();

  const emitPath = (layer: LayerName, pathData: string) => {
    const item = parsePath(scope, pathData);
    if (!item) return;
    for (const pl of sampleItem(item)) {
      if (pl.points.length < 2) continue;
      entities.push({
        kind: "polyline",
        layer,
        points: pl.points,
        closed: pl.closed,
      });
    }
    item.remove();
  };

  for (const rawPiece of project.pieces) {
    // Linked parts export at their cut-layout (Spread) position (#652) — bake
    // the `layout` offset since DXF walks model geometry, not the Paper scene.
    const piece = applyPieceLayout(rawPiece, scope);
    if (includePattern) {
      emitPath("CUT", piece.shapePathData);
      for (const hole of piece.holes) {
        entities.push({
          kind: "circle",
          layer: "HOLE",
          x: hole.position.x,
          y: -hole.position.y,
          r: hole.radius,
        });
      }
      for (const cut of piece.cutouts) {
        emitPath("CUT", cut.pathData);
      }
    }
    for (const line of pieceStitchLines(piece)) {
      if (stitchMode === "off") break;
      if (!isVisible(line)) continue;
      if (stitchMode === "seams") {
        // Seam polylines only (#572) — the row's path, no punched holes.
        if (line.points.length >= 2) {
          entities.push({
            kind: "polyline",
            layer: "STITCH",
            points: line.points.map((pt) => [pt.x, -pt.y] as [number, number]),
            closed: line.closed === true,
          });
        }
        continue;
      }
      // Drop holes that overflow this piece's leather (#910) — or, for the
      // optimized "all holes" mode (#572), emit every computed hole. `piece`
      // is the laid-out copy, so its stitch lines and `shapePathData` share
      // one space — filter placements first, then emit only retained holes.
      for (const pathData of pathsFromPlacements(
        line,
        stitchMode === "all-holes"
          ? stitchHolePlacements(line)
          : placedStitchHoles(line, piece, scope),
      )) {
        emitPath("HOLE", pathData);
      }
    }
    if (includePattern) {
      for (const dec of piece.decorations) {
        if (!dec.visible) continue;
        emitDecoration(scope, entities, dec, library);
      }
    }
  }

  // Construction lines live at the project level (not under any piece).
  if (includeConstruction) {
    for (const line of projectConstructionLines(project)) {
      if (!isVisible(line)) continue;
      emitPath("CONSTRUCTION", line.pathData);
    }
  }

  // Size-calibration boxes (#572), on their own layer so the CAM tool can
  // score them: squares left of the content bounds, one per chosen system.
  if (vector && (vector.calibration.metric || vector.calibration.imperial)) {
    let minX = Infinity;
    let minY = Infinity;
    for (const e of entities) {
      if (e.kind === "circle") {
        minX = Math.min(minX, e.x - e.r);
        minY = Math.min(minY, -e.y - e.r);
      } else {
        for (const [x, y] of e.points) {
          minX = Math.min(minX, x);
          minY = Math.min(minY, -y);
        }
      }
    }
    const anchorX = (Number.isFinite(minX) ? minX : 0) - 8;
    let yCursor = Number.isFinite(minY) ? minY : 0;
    const putSquare = (sizeMm: number) => {
      const x0 = anchorX - sizeMm;
      const y0 = yCursor;
      entities.push({
        kind: "polyline",
        layer: "CALIBRATION",
        points: [
          [x0, -y0],
          [x0 + sizeMm, -y0],
          [x0 + sizeMm, -(y0 + sizeMm)],
          [x0, -(y0 + sizeMm)],
        ],
        closed: true,
      });
      yCursor += sizeMm + 8;
    };
    if (vector.calibration.metric) putSquare(30); // 3 cm
    if (vector.calibration.imperial) putSquare(2 * 25.4); // 2 in
  }

  scope.project.remove();
  return entities;
}

function emitDecoration(
  scope: paper.PaperScope,
  entities: DxfEntity[],
  dec: Decoration,
  _library: StampLibrary,
) {
  switch (dec.kind) {
    case "offset": {
      const src = parsePath(scope, dec.sourcePathData);
      if (!src) return;
      try {
        const off = PaperOffset.offset(src, dec.distance, {
          join: dec.joinType,
          cap: "butt",
        });
        for (const pl of sampleItem(off)) {
          if (pl.points.length < 2) continue;
          entities.push({
            kind: "polyline",
            layer: "ENGRAVE",
            points: pl.points,
            closed: pl.closed,
          });
        }
        off.remove();
      } catch (e) {
        console.warn("offset DXF export failed", e);
      }
      src.remove();
      return;
    }
    case "freePath": {
      const p = parsePath(scope, dec.pathData);
      if (!p) return;
      for (const pl of sampleItem(p)) {
        if (pl.points.length < 2) continue;
        entities.push({
          kind: "polyline",
          layer: "ENGRAVE",
          points: pl.points,
          closed: pl.closed,
        });
      }
      p.remove();
      return;
    }
    // Stamps and fills are decorative impressions; we omit them from DXF since
    // laser cutters can't reproduce stamp depth and Cricut can't import their
    // clip/tile geometry. Keeping them out keeps the DXF a clean cut file.
    default:
      return;
  }
}

// ---------------------------------------------------------------------------
// Paper.js sampling helpers.
// ---------------------------------------------------------------------------

interface PolylineSamples {
  points: [number, number][];
  closed: boolean;
}

/** Sample a paper item into one or more polylines, in CAD coordinates (y-up).
 *  The y axis is flipped here so every consumer shares the same handedness. */
function sampleItem(item: paper.Item): PolylineSamples[] {
  const out: PolylineSamples[] = [];
  collect(item, out);
  return out;
}

function collect(item: paper.Item, out: PolylineSamples[]) {
  if (item instanceof (paper as unknown as { Path: typeof paper.Path }).Path) {
    out.push(samplePath(item as paper.Path));
    return;
  }
  if (
    item instanceof
    (paper as unknown as { CompoundPath: typeof paper.CompoundPath })
      .CompoundPath
  ) {
    for (const child of (item as paper.CompoundPath).children ?? []) {
      collect(child, out);
    }
    return;
  }
  if (item.children) {
    for (const child of item.children) collect(child, out);
  }
}

function samplePath(path: paper.Path): PolylineSamples {
  const pts: [number, number][] = [];
  const segs = path.segments ?? [];
  if (!segs.length) return { points: [], closed: !!path.closed };
  for (let i = 0; i < segs.length; i += 1) {
    const seg = segs[i];
    if (!seg) continue;
    const next = segs[i + 1] ?? (path.closed ? segs[0] : null);
    pts.push([seg.point.x, -seg.point.y]);
    if (
      next &&
      (seg.handleOut.length > 0.001 || next.handleIn.length > 0.001)
    ) {
      const curve = seg.curve;
      if (curve) {
        for (let t = 1; t < SAMPLES_PER_CURVE; t += 1) {
          const u = t / SAMPLES_PER_CURVE;
          const p = curve.getPointAtTime(u);
          pts.push([p.x, -p.y]);
        }
      }
    }
  }
  return { points: pts, closed: !!path.closed };
}

function parsePath(
  scope: paper.PaperScope,
  d: string,
): paper.Path | paper.CompoundPath | null {
  if (!d) return null;
  try {
    const cp = new scope.CompoundPath(d);
    if (cp.children?.length === 1) {
      const single = cp.children[0] as paper.Path;
      single.remove();
      cp.remove();
      return single;
    }
    return cp;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Downloads.
// ---------------------------------------------------------------------------

export function downloadDxf(
  project: Project,
  library: StampLibrary,
  options: { vector?: VectorExportSettings } = {},
) {
  const blob = exportDxf(project, library, options);
  triggerDownload(blob, `${sanitize(project.name)}.dxf`);
}

function sanitize(s: string): string {
  return s.replace(/[^a-z0-9_-]/gi, "_") || "leather-art";
}
